import type React from 'react'

import { useState, useRef } from 'react'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Download, Trash2, Loader2, Upload, File, X, Check, FileText, FilePlus } from 'lucide-react'
import { FeedbackDialog } from './feedback-dialog'
import { useCreatedAnexoApuracao } from '@/hooks/useCreatedAnexoApuracao'
import { useDeletedAnexoCreditByID } from '@/hooks/useDeletedAnexoCreditByID'

import { useFetchAnexosByID } from '@/hooks/useFetchAnexosByID'
import { cn } from '@/lib/utils'
import { formatDate } from '@/utils/dates'

interface FileManagerProps {
  id: string
  id_client: string
  id_apuracao: string
  id_product: string
  id_apuracao_competencia: string
  type: 'credit' | 'trimestre' | 'other'
  onClose?: () => void
}

const sugestoesPrev = [
  'RECIBO DCTF WEB',
  'RECIBO PER/DCOMP',
  'DECLARAÇÃO COMPLETA',
  'DARF COMPLEMENTAR PARA PAGAMENTO',
  //O RESTO A BAIXO NAO E ENVIADO PARA O CLIENTE
  'OUTROS',
  'TABELA DE INCIDÊNCIA', 
  'RESUMOS SINTÉTICOS'
]

const sugestoesAliq = ['PERS', 'COMUNICADO DE COMPENSAÇÃo DE OFÍCIO', 'AVISO DE RECEBIMENTO', 'OUTROS']

export function FileManager({ id, type, id_client, id_apuracao, id_product,id_apuracao_competencia, onClose }: FileManagerProps) {
  const [fileName, setFileName] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  const [uploadStep, setUploadStep] = useState<1 | 2>(1)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const createAnexoApuracao = useCreatedAnexoApuracao()
  const deleteAnexoCredit = useDeletedAnexoCreditByID()
  const { data: anexosCredit = [], refetch: refetchCredit } = useFetchAnexosByID(id, id_apuracao)
  const [deleting, setDeleting] = useState<string | null>(null)

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0])
      setUploadStep(2)
    }
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setIsDragging(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setFile(e.dataTransfer.files[0])
      setUploadStep(2)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!file || !fileName) return

    createAnexoApuracao.mutate(
      {
        id_client: id_client,
        id_apuracao,
        id_calculo: id,
        nome: fileName,
        id_product,
        id_apuracao_competencia,
        anexo: file
      },
      {
        onSuccess: () => {
          setMessage({ type: 'success', text: 'Arquivo adicionado com sucesso!' })
          setIsDialogOpen(true)
          refetchCredit()
          setFileName('')
          setFile(null)
          setUploadStep(1)
        },
        onError: (error: any) => {
          console.error('Erro ao adicionar arquivo:', error)
          setMessage({ type: 'error', text: `Erro ao adicionar arquivo: ${error.message}` })
          setIsDialogOpen(true)
        }
      }
    )
  }

  const handleDelete = async (anexoId: string) => {
    setDeleting(anexoId)
    deleteAnexoCredit.mutate(anexoId, {
      onSuccess: () => {
        setMessage({ type: 'success', text: 'Arquivo excluído com sucesso!' })
        setIsDialogOpen(true)
        refetchCredit()
        setDeleting(null)
      },
      onError: (error: any) => {
        setMessage({ type: 'error', text: `Erro ao excluir: ${error.message}` })
        setIsDialogOpen(true)
        setDeleting(null)
      }
    })
  }

  const handleDownload = (url: string) => {
    try {
      window.open(url, '_blank')
    } catch (error: any) {
      console.error('Erro ao baixar anexo:', error)
      setMessage({ type: 'error', text: `Erro ao baixar: ${error.message}` })
      setIsDialogOpen(true)
    }
  }

  const resetUpload = () => {
    setFile(null)
    setFileName('')
    setUploadStep(1)
  }

  const getFileIcon = (filename: string) => {
    const extension = filename.split('.').pop()?.toLowerCase()

    switch (extension) {
      case 'pdf':
        return <FileText className="w-4 h-4 text-red-500" />
      case 'doc':
      case 'docx':
        return <FileText className="w-4 h-4 text-blue-500" />
      case 'csv':
      case 'xls':
      case 'xlsx':
        return <FileText className="w-4 h-4 text-green-500" />
      default:
        return <File className="w-4 h-4 text-gray-500" />
    }
  }

  return (
    <div className="w-full max-w-md mx-auto bg-white rounded-lg border shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h3 className="text-sm font-medium">Gerenciador de Anexos</h3>
        {onClose && (
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100" aria-label="Fechar" title="Fechar">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      <Tabs defaultValue="upload" className="w-full h-[290px] flex flex-col">
         <TabsList className="grid w-full grid-cols-2 rounded-none">
          <TabsTrigger value="upload" className="rounded-none">
            <FilePlus className="w-4 h-4 mr-2" />
            Adicionar
          </TabsTrigger>
          <TabsTrigger value="list" className="rounded-none">
            <FileText className="w-4 h-4 mr-2" />
            Anexos ({anexosCredit.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upload" className="flex-1 p-4 overflow-auto">
          {uploadStep === 1 ? (
            <div className="space-y-4 h-full flex flex-col">
              <div
                className={cn(
                  'border-2 border-dashed rounded-lg flex-1 flex flex-col items-center justify-center p-6 transition-colors',
                  isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
                )}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.csv,.xls,.xlsx"
                  className="hidden"
                />
                <Upload className={cn('w-10 h-10 mb-2', isDragging ? 'text-blue-500' : 'text-gray-400')} />
                <p className="text-sm font-medium text-center">
                  {isDragging ? 'Solte o arquivo aqui' : 'Arraste e solte ou clique para selecionar'}
                </p>
                <p className="text-xs text-gray-500 mt-1 text-center">PDF, DOC, DOCX, CSV, XLS, XLSX</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4 h-full flex flex-col">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                {file && (
                  <>
                    {getFileIcon(file.name)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{file.name}</p>
                      <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-500" onClick={resetUpload}>
                      <X className="h-4 w-4" />
                    </Button>
                  </>
                )}
              </div>
              <div className="space-y-3 flex-1">
                <Label htmlFor="fileName">Nome do Arquivo</Label>

                {type === 'other' ? (
                  // INPUT LIVRE quando type === 'other'
                  <input
                    id="fileName"
                    type="text"
                    value={fileName}
                    onChange={e => setFileName(e.target.value)}
                    placeholder="Digite um nome para o arquivo"
                    className="w-full p-2 border rounded-md text-sm"
                    required
                  />
                ) : (
                  // SELECT com sugestões para 'credit' e 'trimestre'
                  <select
                    id="fileName"
                    value={fileName}
                    onChange={e => setFileName(e.target.value)}
                    required
                    className="w-full p-2 border rounded-md text-sm"
                  >
                    <option value="" disabled>
                      Selecione um nome de arquivo
                    </option>
                    {(type === 'credit' ? sugestoesPrev : sugestoesAliq).map(nome => (
                      <option key={nome} value={nome}>
                        {nome}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" size="sm" onClick={resetUpload}>
                  Cancelar
                </Button>
                <Button size="sm" onClick={handleSubmit} disabled={!fileName || createAnexoApuracao.isPending}>
                  {createAnexoApuracao.isPending ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" /> Confirmar Upload
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="list" className="flex-1 p-4 overflow-auto">
          {anexosCredit.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center p-6">
              <FileText className="w-12 h-12 text-gray-300 mb-2" />
              <p className="text-sm text-gray-500">Nenhum arquivo anexado</p>
              <p className="text-xs text-gray-400 mt-1">Adicione arquivos na aba "Adicionar"</p>
            </div>
          ) : (
            <div className="divide-y">
              {anexosCredit.map((attachment: any) => (
                <div key={attachment.id} className="p-3 hover:bg-gray-50">
                  <div className="flex items-center gap-3">
                    {getFileIcon(attachment.nome)}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{attachment.nome}</p>
                      <p className="text-xs text-gray-500">{formatDate(attachment.createdDate)}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-blue-600"
                        onClick={() => handleDownload(attachment.anexo)}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-red-600"
                        onClick={() => handleDelete(attachment.id)}
                        disabled={deleting === attachment.id}
                      >
                        {deleting === attachment.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <FeedbackDialog
        isOpen={isDialogOpen}
        onOpenChange={open => {
          setIsDialogOpen(open)
          if (!open && message?.type === 'success') {
            refetchCredit()
          }
        }}
        message={message}
      />
    </div>
  )
}
